import {
  startTransition,
  useEffect,
  useMemo,
  useRef,
} from "react";
import type { DockPanelKey } from "../components/dockPanelLabels";
import {
  createDockPanelPlaybackRefreshScheduler,
  DOCK_PANEL_PLAYBACK_REFRESH_PANELS,
  type DockPanelPlaybackRefreshKey,
} from "../utils/eegDockPanelRefreshScheduler";

export function useDockPanelPlaybackRefresh(params: {
  isPlaybackRunning: boolean;
  isPanelVisible: (panelKey: DockPanelKey) => boolean;
  readPlayheadSeconds: () => number;
  onPlaybackTimesChange: (
    updater: (
      previous: Partial<Record<DockPanelPlaybackRefreshKey, number>>,
    ) => Partial<Record<DockPanelPlaybackRefreshKey, number>>,
  ) => void;
  visibilityRevision: string;
}): void {
  const {
    isPlaybackRunning,
    isPanelVisible,
    readPlayheadSeconds,
    onPlaybackTimesChange,
    visibilityRevision,
  } = params;

  const isPanelVisibleRef = useRef(isPanelVisible);
  const readPlayheadSecondsRef = useRef(readPlayheadSeconds);
  const onPlaybackTimesChangeRef = useRef(onPlaybackTimesChange);

  useEffect(() => {
    isPanelVisibleRef.current = isPanelVisible;
  }, [isPanelVisible]);

  useEffect(() => {
    readPlayheadSecondsRef.current = readPlayheadSeconds;
  }, [readPlayheadSeconds]);

  useEffect(() => {
    onPlaybackTimesChangeRef.current = onPlaybackTimesChange;
  }, [onPlaybackTimesChange]);

  const scheduler = useMemo(
    () =>
      createDockPanelPlaybackRefreshScheduler({
        isActive: () => isPlaybackRunning,
        isPanelVisible: (panelKey) =>
          isPanelVisibleRef.current(panelKey),
        readPlayheadSeconds: () => readPlayheadSecondsRef.current(),
        onPublish: (panelKey, playheadSeconds) => {
          startTransition(() => {
            onPlaybackTimesChangeRef.current((previous) => ({
              ...previous,
              [panelKey]: playheadSeconds,
            }));
          });
        },
      }),
    [isPlaybackRunning],
  );

  useEffect(() => {
    if (!isPlaybackRunning) {
      scheduler.dispose();
      return () => {
        scheduler.dispose();
      };
    }

    const anchorTime = readPlayheadSecondsRef.current();
    startTransition(() => {
      onPlaybackTimesChangeRef.current((previous) => {
        const next = { ...previous };
        for (const panelKey of DOCK_PANEL_PLAYBACK_REFRESH_PANELS) {
          if (
            isPanelVisibleRef.current(panelKey) &&
            next[panelKey] === undefined
          ) {
            next[panelKey] = anchorTime;
          }
        }
        return next;
      });
    });

    scheduler.syncVisiblePanels();

    return () => {
      scheduler.dispose();
    };
  }, [isPlaybackRunning, scheduler]);

  useEffect(() => {
    if (!isPlaybackRunning) {
      return;
    }
    scheduler.syncVisiblePanels();
  }, [isPlaybackRunning, scheduler, visibilityRevision]);
}
